import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useEmployeeReasonTypes,
  useCreateEmployeeReasonType,
  useUpdateEmployeeReasonType,
  useDeleteEmployeeReasonType,
  useEmployeeReasonTypeById,
} from '../../../hooks/queries/useVisitorManagement';
import type {
  EmployeeReasonType,
  EmployeeReasonTypeWrite,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';

interface EmployeeReasonRow {
  [key: string]: unknown;
  id: string;
  name: string;
  code: string;
  requires_supervisor_approval: boolean;
  is_active: boolean;
}

const columns: Column<EmployeeReasonRow>[] = [
  { key: 'name', label: 'Name', sortable: true, width: '180px' },
  { key: 'code', label: 'Code', sortable: true, width: '120px' },
  {
    key: 'requires_supervisor_approval',
    label: 'Requires Approval',
    width: '150px',
    render: (row) =>
      row.requires_supervisor_approval ? (
        <StatusBadge status={'active' as StatusType} label="Yes" />
      ) : null,
  },
  {
    key: 'is_active',
    label: 'Active',
    width: '100px',
    render: (row) => (
      <StatusBadge
        status={
          row.is_active ? ('active' as StatusType) : ('inactive' as StatusType)
        }
        label={row.is_active ? 'Active' : 'Inactive'}
      />
    ),
  },
];

export function useEmployeeReasonTypeCrud() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const isEmployeeReasonTab =
    urlTab?.toLowerCase().replace(/\s+/g, '-') === 'employee-reasons';
  const { data: reasonById, error: reasonByIdError } = useEmployeeReasonTypeById(
    urlId as string,
    isEmployeeReasonTab,
  );
  const { data, isLoading, error } = useEmployeeReasonTypes();
  const createMutation = useCreateEmployeeReasonType();
  const updateMutation = useUpdateEmployeeReasonType();
  const deleteMutation = useDeleteEmployeeReasonType();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<EmployeeReasonType | null>(null);
  const isSingleReasonTypeView = Boolean(
    urlId && isEmployeeReasonTab && reasonById && !reasonByIdError,
  );
  const reasonDetails =
    isSingleReasonTypeView && reasonById ? [reasonById] : (data ?? []);
  const rows: EmployeeReasonRow[] = reasonDetails
    .filter((r): r is EmployeeReasonType => !!r)
    .map((r) => ({
      id: r.public_id,
      name: r.name,
      code: r.code,
      requires_supervisor_approval: r.requires_supervisor_approval,
      is_active: r.is_active,
    }));

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: EmployeeReasonRow) => {
    const item = reasonDetails.find((r) => r?.public_id === row.id);
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: EmployeeReasonTypeWrite,
    setError: UseFormSetError<EmployeeReasonTypeWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof EmployeeReasonTypeWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: values },
        {
          onSuccess: () => {
            toaster.create({ title: 'Employee reason type updated', type: 'success' });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          toaster.create({ title: 'Employee reason type created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Employee reason type deleted', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: EmployeeReasonTypeWrite = useMemo(
    () =>
      editItem
        ? {
            name: editItem.name,
            code: editItem.code,
            description: editItem.description || '',
            requires_supervisor_approval: editItem.requires_supervisor_approval,
            is_active: editItem.is_active,
          }
        : {
            name: '',
            code: '',
            description: '',
            requires_supervisor_approval: false,
            is_active: true,
          },
    [editItem],
  );

  return {
    data: reasonDetails,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  };
}
